import { useState, type KeyboardEvent, type ReactNode } from 'react';
import { formatNumber } from '@/lib/format';
import { useProfileTerms } from '@/lib/hooks';
import { Icon } from './Icon';
import type { IconName } from './icons';
import { MenuHead, MenuItem, MenuSep, usePopoverClose } from './Popover';
import { Spinner } from './bits';
import { hasTerm, normaliseTerm, PROFILE_LIMITS, sameTerm } from '@shared/profiles';

// Pieces of a company profile that the Companies list and the company sheet share: capability chips (a strength, a
// gap, or a gap the team offers) and the menus that pick capabilities and markets from the team's vocabulary.

export type CapabilitySide = 'strengths' | 'lacks';

export const SIDE_LABEL: Record<CapabilitySide, string> = { strengths: 'Strengths', lacks: 'Gaps' };

/** One capability. A gap the team offers stands out, since it is the way in. */
export function CapabilityChip({ name, side, fit, onRemove }: { name: string; side: CapabilitySide; fit?: boolean; onRemove?: () => void }) {
  const cls = ['cap', side === 'strengths' ? 'cap-s' : 'cap-g', fit ? 'fit' : '', onRemove ? 'cap-rm' : ''].filter(Boolean).join(' ');
  return (
    <span className={cls} data-tip={fit ? 'You offer this' : undefined}>
      {fit ? <Icon name="target" /> : null}
      {name}
      {fit ? <span className="sr"> (you offer this)</span> : null}
      {onRemove ? (
        <button type="button" aria-label={`Remove ${name} from ${SIDE_LABEL[side].toLowerCase()}`} onClick={onRemove}>
          <Icon name="x" />
        </button>
      ) : null}
    </span>
  );
}

/** A company's strengths or gaps as chips; gaps the team offers come first. With `limit`, the rest show as "+N". */
export function CapabilityChips({ names, side, offerings, onRemove, limit }: { names: string[]; side: CapabilitySide; offerings: string[]; onRemove?: (name: string) => void; limit?: number }) {
  const fit = (n: string) => side === 'lacks' && hasTerm(offerings, n);
  const ordered = side === 'lacks' ? [...names.filter(fit), ...names.filter((n) => !fit(n))] : names;
  const shown = limit && ordered.length > limit ? ordered.slice(0, limit) : ordered;
  const rest = ordered.slice(shown.length);
  return (
    <>
      {shown.map((n) => (
        <CapabilityChip key={n} name={n} side={side} fit={fit(n)} onRemove={onRemove ? () => onRemove(n) : undefined} />
      ))}
      {rest.length ? (
        <span className="muted more-n" data-tip={rest.join(', ')}>
          +{rest.length}
          <span className="sr">: {rest.join(', ')}</span>
        </span>
      ) : null}
    </>
  );
}

/** A menu row that can be ticked on and off. */
function ToggleItem({ icon, checked, sub, onToggle, children }: { icon: IconName; checked: boolean; sub?: ReactNode; onToggle: () => void; children: ReactNode }) {
  return (
    <button type="button" className="mi" role="menuitemcheckbox" aria-checked={checked} onClick={onToggle}>
      <Icon name={icon} />
      <span className="grow">{children}</span>
      {sub ? <span className="sub">{sub}</span> : null}
      <Icon name="check" className={checked ? 'mi-on' : 'mi-on mi-off'} />
    </button>
  );
}

function FindBox({ value, onChange, label, max, onEnter }: { value: string; onChange: (v: string) => void; label: string; max: number; onEnter: () => void }) {
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onEnter();
    }
  };
  return (
    <div className="pop-search">
      <Icon name="search" />
      <input className="cell-input" value={value} placeholder={label} aria-label={label} maxLength={max} autoComplete="off" spellCheck={false} onChange={(e) => onChange(e.target.value)} onKeyDown={onKeyDown} />
    </div>
  );
}

/**
 * Ticks capabilities on and off one side of a company's profile. Ticking one the company has on the other side
 * moves it across. A name nobody uses yet can be added as typed.
 */
export function CapabilityItems({ side, strengths, lacks, onToggle }: { side: CapabilitySide; strengths: string[]; lacks: string[]; onToggle: (name: string) => void }) {
  const terms = useProfileTerms();
  const [q, setQ] = useState('');
  const typed = normaliseTerm(q);
  const on = side === 'strengths' ? strengths : lacks;
  const other = side === 'strengths' ? lacks : strengths;
  const known = terms.data?.capabilities ?? [];
  const all = [...on.filter((n) => !known.some((c) => sameTerm(c.name, n))).map((name) => ({ name, offered: false })), ...known];
  const shown = (typed ? all.filter((c) => c.name.toLowerCase().includes(typed.toLowerCase())) : all).slice(0, 80);
  const exact = all.find((c) => sameTerm(c.name, typed));
  const canAdd = typed.length > 0 && typed.length <= PROFILE_LIMITS.capability && !exact;
  const pick = (name: string) => {
    onToggle(name);
    setQ('');
  };
  const subFor = (name: string, offered: boolean) => {
    if (hasTerm(other, name)) return side === 'strengths' ? 'In gaps' : 'In strengths';
    return offered ? 'You offer' : undefined;
  };
  return (
    <>
      <MenuHead>{SIDE_LABEL[side]}</MenuHead>
      <FindBox
        value={q}
        onChange={setQ}
        label={side === 'strengths' ? 'Find or add a strength' : 'Find or add a gap'}
        max={PROFILE_LIMITS.capability}
        onEnter={() => {
          if (exact) pick(exact.name);
          else if (canAdd) pick(typed);
          else if (shown[0]) pick(shown[0].name);
        }}
      />
      <div className="pop-list">
        {terms.isPending ? (
          <p className="pop-note" role="status">
            <Spinner label="Loading capabilities" /> Loading capabilities
          </p>
        ) : null}
        {terms.isError ? <p className="pop-note">The team's capabilities did not load. Type one and press Enter.</p> : null}
        {shown.map((c) => (
          <ToggleItem key={c.name} icon={c.offered ? 'target' : 'layers'} checked={hasTerm(on, c.name)} sub={subFor(c.name, c.offered)} onToggle={() => pick(c.name)}>
            {c.name}
          </ToggleItem>
        ))}
        {terms.data && !shown.length && !canAdd ? <p className="pop-note">{typed ? `Nothing matches “${typed}”` : 'Type a capability, such as Backend, and press Enter.'}</p> : null}
      </div>
      {canAdd ? (
        <>
          <MenuSep />
          <MenuItem icon="plus" keep onSelect={() => pick(typed)}>
            Add “{typed}”
          </MenuItem>
        </>
      ) : null}
    </>
  );
}

/** Picks one market from those in use, or a new one as typed. "No market" shows when one is set, or always with `clearable`. */
export function MarketItems({ current, onPick, head = 'Market', clearable }: { current: string | null; onPick: (market: string | null) => void; head?: ReactNode; clearable?: boolean }) {
  const terms = useProfileTerms();
  const close = usePopoverClose();
  const [q, setQ] = useState('');
  const typed = normaliseTerm(q);
  const all = terms.data?.markets ?? [];
  const shown = typed ? all.filter((m) => m.name.toLowerCase().includes(typed.toLowerCase())) : all;
  const exact = all.find((m) => sameTerm(m.name, typed));
  const canAdd = typed.length > 0 && typed.length <= PROFILE_LIMITS.market && !exact;
  const choose = (market: string | null) => {
    close();
    onPick(market);
  };
  return (
    <>
      <MenuHead>{head}</MenuHead>
      <FindBox
        value={q}
        onChange={setQ}
        label="Find or add a market"
        max={PROFILE_LIMITS.market}
        onEnter={() => {
          if (exact) choose(exact.name);
          else if (canAdd) choose(typed);
          else if (shown[0]) choose(shown[0].name);
        }}
      />
      <div className="pop-list">
        {terms.isPending ? (
          <p className="pop-note" role="status">
            <Spinner label="Loading markets" /> Loading markets
          </p>
        ) : null}
        {terms.isError ? <p className="pop-note">The team's markets did not load. Type one and press Enter.</p> : null}
        {shown.map((m) => (
          <MenuItem key={m.name} icon="globe" checked={!!current && sameTerm(current, m.name)} sub={formatNumber(m.companies)} onSelect={() => onPick(m.name)}>
            {m.name}
          </MenuItem>
        ))}
        {terms.data && !shown.length && !canAdd ? <p className="pop-note">{typed ? `Nothing matches “${typed}”` : 'Type a market, such as AECO, and press Enter.'}</p> : null}
      </div>
      {canAdd || current || clearable ? <MenuSep /> : null}
      {canAdd ? (
        <MenuItem icon="plus" onSelect={() => onPick(typed)}>
          Add “{typed}”
        </MenuItem>
      ) : null}
      {current || clearable ? (
        <MenuItem icon="x" onSelect={() => onPick(null)}>
          No market
        </MenuItem>
      ) : null}
    </>
  );
}

/**
 * Picks one capability from the team's vocabulary, for a change to many companies at once. With `allowAdd`, a name
 * nobody uses yet can be picked as typed; without it, only capabilities companies list on `side` are offered.
 */
export function CapabilityPickItems({ side, head, allowAdd, onPick }: { side: CapabilitySide; head: ReactNode; allowAdd: boolean; onPick: (name: string) => void }) {
  const terms = useProfileTerms();
  const close = usePopoverClose();
  const [q, setQ] = useState('');
  const typed = normaliseTerm(q);
  const count = (c: { strengths: number; lacks: number }) => (side === 'strengths' ? c.strengths : c.lacks);
  const all = (terms.data?.capabilities ?? []).filter((c) => allowAdd || count(c) > 0);
  const shown = (typed ? all.filter((c) => c.name.toLowerCase().includes(typed.toLowerCase())) : all).slice(0, 80);
  const exact = all.find((c) => sameTerm(c.name, typed));
  const canAdd = allowAdd && typed.length > 0 && typed.length <= PROFILE_LIMITS.capability && !exact;
  const choose = (name: string) => {
    close();
    onPick(name);
  };
  return (
    <>
      <MenuHead>{head}</MenuHead>
      <FindBox
        value={q}
        onChange={setQ}
        label={allowAdd ? 'Find or add a capability' : 'Find a capability'}
        max={PROFILE_LIMITS.capability}
        onEnter={() => {
          if (exact) choose(exact.name);
          else if (canAdd) choose(typed);
          else if (shown[0]) choose(shown[0].name);
        }}
      />
      <div className="pop-list">
        {terms.isPending ? (
          <p className="pop-note" role="status">
            <Spinner label="Loading capabilities" /> Loading capabilities
          </p>
        ) : null}
        {terms.isError ? <p className="pop-note">The team's capabilities did not load.{allowAdd ? ' Type one and press Enter.' : ''}</p> : null}
        {shown.map((c) => (
          <MenuItem key={c.name} icon={c.offered ? 'target' : 'layers'} sub={count(c) ? formatNumber(count(c)) : undefined} onSelect={() => onPick(c.name)}>
            {c.name}
          </MenuItem>
        ))}
        {terms.data && !shown.length && !canAdd ? (
          <p className="pop-note">{typed ? `Nothing matches “${typed}”` : allowAdd ? 'Type a capability, such as Backend, and press Enter.' : `No company lists ${side === 'strengths' ? 'a strength' : 'a gap'} yet.`}</p>
        ) : null}
      </div>
      {canAdd ? (
        <>
          <MenuSep />
          <MenuItem icon="plus" onSelect={() => onPick(typed)}>
            Add “{typed}”
          </MenuItem>
        </>
      ) : null}
    </>
  );
}
